// src/services/FileStorageService.js - IndexedDB를 사용한 파일 저장
class FileStorageService {
  constructor() {
    this.dbName = 'SillyeonJangDB';
    this.dbVersion = 1;
    this.storeName = 'pdfFiles';
    this.db = null;
  }

  // IndexedDB 초기화
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Object Store가 존재하지 않으면 생성
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('uploadDate', 'uploadDate', { unique: false });
          store.createIndex('title', 'title', { unique: false });
        }
      };
    });
  }

  // 파일 저장
  async saveFile(fileData, file) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // 파일을 ArrayBuffer로 변환
      const reader = new FileReader();
      reader.onload = () => {
        const savedData = {
          ...fileData,
          fileBuffer: reader.result, // ArrayBuffer로 저장
          fileType: file.type,
          fileName: file.name
        };

        const request = store.add(savedData);
        request.onsuccess = () => {
          console.log('✅ IndexedDB에 파일 저장 완료:', fileData.id);
          resolve(savedData);
        };
        request.onerror = () => reject(request.error);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  // 모든 파일 목록 가져오기
  async getAllFiles() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        const files = request.result.map(fileData => ({
          ...fileData,
          // Blob URL 생성 (새로고침 후에도 유효)
          downloadURL: this.createBlobURL(fileData.fileBuffer, fileData.fileType)
        }));
        resolve(files);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // 특정 파일 가져오기
  async getFile(id) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);

      request.onsuccess = () => {
        if (request.result) {
          const fileData = {
            ...request.result,
            downloadURL: this.createBlobURL(request.result.fileBuffer, request.result.fileType)
          };
          resolve(fileData);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // 파일 삭제
  async deleteFile(id) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);

      request.onsuccess = () => {
        console.log('✅ 파일 삭제 완료:', id);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ArrayBuffer를 Blob URL로 변환
  createBlobURL(arrayBuffer, mimeType) {
    const blob = new Blob([arrayBuffer], { type: mimeType });
    return URL.createObjectURL(blob);
  }

  // 다운로드 가능한 링크 생성
  async createDownloadLink(id, fileName) {
    try {
      const fileData = await this.getFile(id);
      if (!fileData) {
        throw new Error('파일을 찾을 수 없습니다.');
      }

      const link = document.createElement('a');
      link.href = fileData.downloadURL;
      link.download = fileName || fileData.fileName;
      return link;
    } catch (error) {
      console.error('다운로드 링크 생성 실패:', error);
      throw error;
    }
  }

  // 저장 공간 정리
  cleanup() {
    // 사용하지 않는 Blob URL들 정리
    // 실제로는 URL.revokeObjectURL()을 사용해야 하지만
    // 여기서는 간단히 처리
    console.log('🧹 저장 공간 정리 완료');
  }
}

// 싱글톤 인스턴스 생성
const fileStorageService = new FileStorageService();
export default fileStorageService;